/**
 * bookingService.ts
 *
 * Service layer for booking-related API calls.
 */

import { supabase } from '@/lib/supabaseClient';
import type { BookingFormData, Booking, BarberService, TimeSlot, AvailabilityRow, BlockedSlot } from '../types';

// ─── Schedule Configuration ────────────────────────────────────────────────────

const SLOT_DURATION = 30; // minutes

/** Convert "HH:MM" or "HH:MM:SS" to total minutes since midnight */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Convert total minutes since midnight to "HH:MM" */
function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Generate slot start times within a working window, skipping all break intervals.
 * Supports multiple breaks via the `breaks` JSONB field.
 * Falls back to legacy break_start/break_end if breaks array is empty.
 */
function generateSlotsForDay(avRow: AvailabilityRow | null): string[] {
  const slots: string[] = [];

  if (!avRow || !avRow.is_working) return slots;

  const dayStart = timeToMinutes(avRow.start_time);
  const dayEnd   = timeToMinutes(avRow.end_time);

  // Build break ranges — prefer new `breaks` array, fall back to legacy fields
  const breakRanges: Array<{ start: number; end: number }> = [];
  if (avRow.breaks && avRow.breaks.length > 0) {
    for (const brk of avRow.breaks) {
      if (brk.start && brk.end) {
        breakRanges.push({ start: timeToMinutes(brk.start), end: timeToMinutes(brk.end) });
      }
    }
  } else if (avRow.break_start && avRow.break_end) {
    breakRanges.push({
      start: timeToMinutes(avRow.break_start),
      end:   timeToMinutes(avRow.break_end),
    });
  }

  let cursor = dayStart;
  while (cursor + SLOT_DURATION <= dayEnd) {
    const slotEnd = cursor + SLOT_DURATION;
    const overlapsBreak = breakRanges.some(
      (b) => cursor < b.end && slotEnd > b.start
    );
    if (!overlapsBreak) {
      slots.push(minutesToTime(cursor));
    }
    cursor += SLOT_DURATION;
  }

  return slots;
}

/** Default fallback blocks when no availability is configured */
const DEFAULT_BLOCKS = [
  { start: '08:30', end: '12:00' },
  { start: '13:30', end: '18:00' },
];

/**
 * Default working days when no availability rows exist in the DB.
 * 0 = Sunday (off), 1–6 = Mon–Sat (working).
 */
const DEFAULT_WORKING_DAYS: Record<number, boolean> = {
  0: false, // Sunday  → off
  1: true,  // Monday
  2: true,  // Tuesday
  3: true,  // Wednesday
  4: true,  // Thursday
  5: true,  // Friday
  6: true,  // Saturday
};

function generateDefaultSlots(): string[] {
  const slots: string[] = [];
  for (const block of DEFAULT_BLOCKS) {
    const blockStart = timeToMinutes(block.start);
    const blockEnd   = timeToMinutes(block.end);
    let cursor = blockStart;
    while (cursor + SLOT_DURATION <= blockEnd) {
      slots.push(minutesToTime(cursor));
      cursor += SLOT_DURATION;
    }
  }
  return slots;
}

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Fetch all active services from Supabase, ordered by created_at.
 */
export async function getServices(): Promise<BarberService[]> {
  const { data, error } = await supabase
    .from('services')
    .select('id, name, price, duration_minutes, description, short_description, landing_description, booking_fee')
    .eq('active', true)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[bookingService] getServices error:', error);
    return [];
  }

  return (data ?? []).map((row) => ({
    id: row.id as string,
    name: row.name as string,
    price: row.price as number,
    currency: '$',
    duration: row.duration_minutes as number,
    description: (row.short_description as string | null) ?? (row.description as string | null) ?? undefined,
    landing_description: (row.landing_description as string | null) ?? (row.description as string | null) ?? undefined,
    booking_fee: (row.booking_fee as number) ?? 0,
  }));
}

/**
 * Fetch the active barber's id (cached-friendly, lightweight).
 */
async function fetchActiveBarberId(): Promise<string | null> {
  const { data, error } = await supabase
    .from('barbers')
    .select('id')
    .eq('active', true)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data.id as string;
}

/**
 * Fetch availability + blocked_slots for a given barber and date.
 * Returns { avRow, blockedSlots } — both public-readable.
 */
async function fetchDayContext(barberId: string, date: string): Promise<{
  avRow: AvailabilityRow | null;
  blockedSlots: BlockedSlot[];
}> {
  // day_of_week: JS Date uses 0=Sun … 6=Sat, matching our DB convention
  const [year, month, day] = date.split('-').map(Number);
  const dow = new Date(year, month - 1, day).getDay();

  const [avResult, bsResult] = await Promise.all([
    supabase
      .from('availability')
      .select('*')
      .eq('barber_id', barberId)
      .eq('day_of_week', dow)
      .maybeSingle(),
    supabase
      .from('blocked_slots')
      .select('*')
      .eq('barber_id', barberId)
      .eq('blocked_date', date),
  ]);

  return {
    avRow: (avResult.data as AvailabilityRow | null) ?? null,
    blockedSlots: (bsResult.data ?? []) as BlockedSlot[],
  };
}

/**
 * Generate available time slots for a given date.
 *
 * Logic:
 * 1. Determine working hours from `availability` table (day_of_week match).
 *    - If no row exists → use default blocks (backward-compatible).
 *    - If is_working = false → return empty (day off).
 * 2. Remove slots that fall inside any `blocked_slots` range for that date.
 *    - Full-day block (start_time IS NULL) → return empty.
 *    - Partial block → remove overlapping slots.
 * 3. Remove slots that overlap with existing confirmed/pending appointments.
 */
export async function getAvailableTimeSlots(date: string): Promise<TimeSlot[]> {
  const barberId = await fetchActiveBarberId();

  // ── 1. Determine base slots from availability ──────────────────────────────
  let baseSlots: string[];

  if (!barberId) {
    // No barber found — fall back to defaults
    baseSlots = generateDefaultSlots();
  } else {
    const { avRow, blockedSlots } = await fetchDayContext(barberId, date);

    // Determine if the day is a working day
    const [year, month, day] = date.split('-').map(Number);
    const dow = new Date(year, month - 1, day).getDay();

    // If availability row says not working → no slots
    if (avRow && !avRow.is_working) {
      return [];
    }

    // If no availability row exists, check default working days
    if (!avRow && !DEFAULT_WORKING_DAYS[dow]) {
      return [];
    }

    // Check for a full-day block
    const hasFullDayBlock = blockedSlots.some((bs) => bs.start_time === null);
    if (hasFullDayBlock) {
      return [];
    }

    // Generate slots from availability row (or defaults if none configured)
    baseSlots = avRow ? generateSlotsForDay(avRow) : generateDefaultSlots();

    // ── 2. Remove slots blocked by blocked_slots (partial ranges) ─────────────
    const partialBlocks = blockedSlots
      .filter((bs) => bs.start_time !== null && bs.end_time !== null)
      .map((bs) => ({
        start: timeToMinutes(bs.start_time!.slice(0, 5)),
        end:   timeToMinutes(bs.end_time!.slice(0, 5)),
      }));

    if (partialBlocks.length > 0) {
      baseSlots = baseSlots.filter((slotTime) => {
        const slotStart = timeToMinutes(slotTime);
        const slotEnd   = slotStart + SLOT_DURATION;
        return !partialBlocks.some(
          (b) => slotStart < b.end && slotEnd > b.start
        );
      });
    }
  }

  // ── 3. Remove slots taken by existing appointments ─────────────────────────
  // Only 'confirmed' and 'pending' block slots.
  // 'no_show', 'cancelled', 'completed' do NOT block — slot is free again.
  const { data: apptData, error: apptErr } = await supabase
    .from('appointments')
    .select('start_time, end_time')
    .eq('appointment_date', date)
    .in('status', ['confirmed', 'pending']);

  if (apptErr) {
    console.error('[bookingService] getAvailableTimeSlots appointments error:', apptErr);
    return baseSlots.map((t) => ({ value: t, label: t, available: true }));
  }

  const bookedRanges = (apptData ?? []).map((row) => ({
    start: timeToMinutes(row.start_time.slice(0, 5)),
    end:   timeToMinutes(row.end_time.slice(0, 5)),
  }));

  return baseSlots.map((slotTime) => {
    const slotStart = timeToMinutes(slotTime);
    const slotEnd   = slotStart + SLOT_DURATION;
    const isBooked  = bookedRanges.some(
      (booked) => slotStart < booked.end && slotEnd > booked.start
    );
    return { value: slotTime, label: slotTime, available: !isBooked };
  });
}

// ─── Calendar Availability ────────────────────────────────────────────────────

export interface DayAvailabilityStatus {
  date: string;
  status: 'available' | 'off' | 'blocked';
}

/**
 * Fetch availability status for every day in a given month.
 * Used by BookingCalendar to visually mark off/blocked days.
 *
 * If no availability rows exist in the DB, falls back to DEFAULT_WORKING_DAYS
 * (Mon–Sat working, Sunday off) so the calendar always reflects reality.
 */
export async function getMonthAvailability(
  year: number,
  month: number // 0-indexed (JS convention)
): Promise<DayAvailabilityStatus[]> {
  const barberId = await fetchActiveBarberId();
  if (!barberId) return [];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDate  = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

  const [avResult, bsResult] = await Promise.all([
    supabase.from('availability').select('day_of_week, is_working').eq('barber_id', barberId),
    supabase
      .from('blocked_slots')
      .select('blocked_date, start_time')
      .eq('barber_id', barberId)
      .gte('blocked_date', firstDate)
      .lte('blocked_date', lastDate),
  ]);

  // Build working-day map: if no rows exist, use defaults
  const avRows = avResult.data ?? [];
  const avMap = new Map<number, boolean>();
  if (avRows.length > 0) {
    for (const row of avRows) {
      avMap.set(row.day_of_week as number, row.is_working as boolean);
    }
  }

  const isWorkingDay = (dow: number): boolean => {
    if (avMap.has(dow)) return avMap.get(dow)!;
    // No row for this day-of-week → use default
    return DEFAULT_WORKING_DAYS[dow] ?? true;
  };

  // Full-day blocks: blocked_date → true
  const fullDayBlocked = new Set<string>();
  for (const bs of (bsResult.data ?? [])) {
    if (bs.start_time === null) {
      fullDayBlocked.add(bs.blocked_date as string);
    }
  }

  const result: DayAvailabilityStatus[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dow = new Date(year, month, d).getDay();

    if (fullDayBlocked.has(dateStr)) {
      result.push({ date: dateStr, status: 'blocked' });
    } else if (!isWorkingDay(dow)) {
      result.push({ date: dateStr, status: 'off' });
    } else {
      result.push({ date: dateStr, status: 'available' });
    }
  }

  return result;
}

// ─── Appointment Creation ─────────────────────────────────────────────────────

export interface CreateAppointmentData {
  client_name: string;
  client_email: string;
  client_phone?: string;
  service_id: string;
  appointment_date: string; // 'YYYY-MM-DD'
  start_time: string;       // 'HH:MM'
}

export interface AppointmentRecord {
  id: string;
  client_name: string;
  client_email: string;
  service_id: string;
  barber_id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  created_at: string;
}

/**
 * Fetch the first active barber's id from Supabase.
 */
async function getActiveBarberId(): Promise<string> {
  const { data, error } = await supabase
    .from('barbers')
    .select('id')
    .eq('active', true)
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    throw new Error('[bookingService] No active barber found');
  }
  return data.id as string;
}

/**
 * Upsert a customer record by email (deduplication).
 * If a customer with the same email exists, update name/phone if provided.
 * Returns the customer id.
 */
async function upsertCustomer(params: {
  name: string;
  email: string;
  phone?: string;
}): Promise<string | null> {
  try {
    const { data: existing, error: selectErr } = await supabase
      .from('customers')
      .select('id, phone')
      .eq('email', params.email.toLowerCase().trim())
      .maybeSingle();

    if (selectErr) {
      console.warn('[bookingService] upsertCustomer select error:', selectErr);
      return null;
    }

    if (existing) {
      // Update name and phone if we have new info
      const updates: Record<string, string> = { name: params.name };
      if (params.phone && !existing.phone) updates.phone = params.phone;
      await supabase.from('customers').update(updates).eq('id', existing.id);
      return existing.id as string;
    }

    // Insert new customer
    const { data: inserted, error: insertErr } = await supabase
      .from('customers')
      .insert({
        name:  params.name,
        email: params.email.toLowerCase().trim(),
        phone: params.phone ?? null,
      })
      .select('id')
      .maybeSingle();

    if (insertErr || !inserted) {
      console.warn('[bookingService] upsertCustomer insert error:', insertErr);
      return null;
    }

    return inserted.id as string;
  } catch (err) {
    console.warn('[bookingService] upsertCustomer unexpected error:', err);
    return null;
  }
}

/**
 * Create a new appointment in Supabase with status='pending'.
 * - Calculates end_time = start_time + 30 minutes
 * - Fetches the active barber_id automatically
 * - Upserts customer record (CRM deduplication by email)
 * - Inserts with status='pending' and payment_status='pending'
 * - Does NOT confirm the appointment — call confirmAppointmentPayment() after payment
 */
export async function createAppointment(
  data: CreateAppointmentData
): Promise<AppointmentRecord> {
  const barber_id = await getActiveBarberId();

  // Calculate end_time
  const startMinutes = timeToMinutes(data.start_time);
  const endMinutes   = startMinutes + SLOT_DURATION;
  const end_time     = minutesToTime(endMinutes);

  // Upsert CRM customer (non-blocking — failure doesn't abort booking)
  const customer_id = await upsertCustomer({
    name:  data.client_name,
    email: data.client_email,
    phone: data.client_phone,
  });

  const { data: inserted, error } = await supabase
    .from('appointments')
    .insert({
      client_name:      data.client_name,
      client_email:     data.client_email,
      client_phone:     data.client_phone ?? null,
      service_id:       data.service_id,
      barber_id,
      appointment_date: data.appointment_date,
      start_time:       data.start_time,
      end_time,
      status:           'pending',
      payment_status:   'pending',
      customer_id:      customer_id ?? null,
    })
    .select()
    .maybeSingle();

  if (error || !inserted) {
    console.error('[bookingService] createAppointment error:', error);
    throw new Error(error?.message ?? 'Failed to create appointment');
  }

  return inserted as AppointmentRecord;
}

/**
 * Confirm an appointment after successful payment.
 * - Updates status = 'confirmed'
 * - Updates payment_status = 'paid'
 *
 * This is the hook point for Wompi (or any payment gateway):
 * call this function once the payment webhook/callback confirms success.
 */
export async function confirmAppointmentPayment(
  appointment_id: string
): Promise<AppointmentRecord> {
  const { data: updated, error } = await supabase
    .from('appointments')
    .update({
      status:         'confirmed',
      payment_status: 'paid',
    })
    .eq('id', appointment_id)
    .select()
    .maybeSingle();

  if (error || !updated) {
    console.error('[bookingService] confirmAppointmentPayment error:', error);
    throw new Error(error?.message ?? 'Failed to confirm appointment payment');
  }

  return updated as AppointmentRecord;
}

// ─── Payment Expiry ───────────────────────────────────────────────────────────

/**
 * Trigger the server-side expiry check for pending payments older than 45 min.
 * Called from the admin panel as a housekeeping step before loading appointments.
 * Fires-and-forgets — errors are logged but never thrown to the caller.
 */
export async function expirePendingPayments(): Promise<void> {
  try {
    await supabase.functions.invoke('expire-pending-payments', { body: {} });
  } catch (err) {
    console.warn('[bookingService] expirePendingPayments: non-critical error', err);
  }
}

// ─── Wompi Payment Integration ────────────────────────────────────────────────

export interface WompiPaymentData {
  appointmentId: string;
  amountInCents: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  reference: string;
}

/**
 * Generate a unique payment reference for Wompi.
 * Format: BSL-{appointmentId-prefix}-{timestamp}
 */
export function generatePaymentReference(appointmentId: string): string {
  const prefix = appointmentId.replace(/-/g, '').slice(0, 8).toUpperCase();
  const ts = Date.now().toString(36).toUpperCase();
  return `BSL-${prefix}-${ts}`;
}

/**
 * Simulate a successful payment without calling Wompi.
 *
 * Mock flow (mirrors real flow):
 * 1. Generate a unique reference
 * 2. Insert a payment row with status='pending' and amount=0 (mock always $0)
 * 3. Wait 1–2 seconds (simulated processing delay)
 * 4. Update payment  → status = 'paid'
 * 5. Update appointment → status = 'confirmed', payment_status = 'paid'
 *
 * Returns the same shape as initWompiPayment so callers can branch cleanly.
 */
export async function mockPayment(params: {
  appointment: AppointmentRecord;
  amountCOP: number;
  customerEmail: string;
  customerName: string;
}): Promise<{ reference: string }> {
  const { appointment } = params;

  const reference = generatePaymentReference(appointment.id);

  // Step 1 — insert pending payment record with amount=0 (mock mode always $0)
  const { error: insertError } = await supabase
    .from('payments')
    .insert({
      appointment_id: appointment.id,
      amount: 0,
      status: 'pending',
      type: 'advance',
      method: 'wompi',
      reference,
    });

  if (insertError) {
    console.error('[bookingService] mockPayment insert error:', insertError);
    throw new Error(insertError.message ?? 'Failed to create mock payment record');
  }

  // Step 2 — simulate processing delay (1–2 seconds)
  const delay = 1000 + Math.random() * 1000;
  await new Promise((resolve) => setTimeout(resolve, delay));

  // Step 3 — mark payment as paid
  const { error: paymentUpdateError } = await supabase
    .from('payments')
    .update({ status: 'paid' })
    .eq('reference', reference);

  if (paymentUpdateError) {
    console.error('[bookingService] mockPayment payment update error:', paymentUpdateError);
    throw new Error(paymentUpdateError.message ?? 'Failed to update mock payment status');
  }

  // Step 4 — confirm appointment
  const { error: apptUpdateError } = await supabase
    .from('appointments')
    .update({
      status: 'confirmed',
      payment_status: 'paid',
    })
    .eq('id', appointment.id);

  if (apptUpdateError) {
    console.error('[bookingService] mockPayment appointment update error:', apptUpdateError);
    throw new Error(apptUpdateError.message ?? 'Failed to confirm appointment in mock mode');
  }

  return { reference };
}

/**
 * Create a payment record in Supabase and return the Wompi checkout URL.
 *
 * Flow:
 * 1. Generate a unique reference
 * 2. Insert a payment row with status='pending' using booking_fee as the charge amount
 * 3. Build the Wompi Widget URL with all required params
 * 4. Return the URL so the caller can redirect the user
 *
 * Note: amountCOP here should be the booking_fee (anticipo), NOT the full service price.
 */
export async function initWompiPayment(params: {
  appointment: AppointmentRecord;
  amountCOP: number;
  customerEmail: string;
  customerName: string;
  redirectUrl: string;
}): Promise<{ wompiUrl: string; reference: string }> {
  const { appointment, amountCOP, customerEmail, customerName, redirectUrl } = params;

  const reference = generatePaymentReference(appointment.id);
  const amountInCents = Math.round(amountCOP * 100);

  // Insert payment record in Supabase
  const { error: paymentError } = await supabase
    .from('payments')
    .insert({
      appointment_id: appointment.id,
      amount: amountCOP,
      status: 'pending',
      reference,
    });

  if (paymentError) {
    console.error('[bookingService] initWompiPayment insert error:', paymentError);
    const isRls = /row-level security policy/i.test(paymentError.message ?? '');
    if (isRls) {
      throw new Error('RLS bloqueó la creación del pago. Revisa las políticas de la tabla payments en Supabase.');
    }
    throw new Error(paymentError.message ?? 'Failed to create payment record');
  }

  // Read Wompi public key from env
  const wompiPublicKey = import.meta.env.VITE_WOMPI_PUBLIC_KEY as string | undefined;
  if (!wompiPublicKey) {
    throw new Error('VITE_WOMPI_PUBLIC_KEY is not configured');
  }

  // Build Wompi checkout URL
  const params2 = new URLSearchParams({
    'public-key': wompiPublicKey,
    currency: 'COP',
    'amount-in-cents': String(amountInCents),
    reference,
    'redirect-url': redirectUrl,
    'customer-data:email': customerEmail,
    'customer-data:full-name': customerName,
  });

  const wompiUrl = `https://checkout.wompi.co/p/?${params2.toString()}`;

  return { wompiUrl, reference };
}
